import { create } from 'zustand';
import api, { type UserNotification } from '../service/api';

interface NotificationsState {
  notifications: UserNotification[];
  newCount: number;
  isLoading: boolean;
  error: string | null;
}

interface NotificationsActions {
  /** Fetch all notifications and new count */
  fetchNotifications: () => Promise<void>;
  /** Fetch only the new count (lightweight) */
  fetchNewCount: () => Promise<void>;
  /** Mark all new notifications as seen */
  markAllAsSeen: () => Promise<void>;
  /** Hide a specific notification */
  hideNotification: (notificationId: string) => Promise<void>;
  /** Reset state */
  reset: () => void;
}

type NotificationsStore = NotificationsState & NotificationsActions;

const initialState: NotificationsState = {
  notifications: [],
  newCount: 0,
  isLoading: false,
  error: null,
};

const useNotificationsStore = create<NotificationsStore>()((set, get) => ({
  ...initialState,

  fetchNotifications: async () => {
    set({ isLoading: true, error: null });
    try {
      const [notifications, { count }] = await Promise.all([
        api.notificationsGetAll(),
        api.notificationsGetNewCount(),
      ]);
      set({ notifications, newCount: count, isLoading: false });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to load notifications';
      set({ error: message, isLoading: false });
    }
  },

  fetchNewCount: async () => {
    try {
      const { count } = await api.notificationsGetNewCount();
      set({ newCount: count });
    } catch {
      // Silently fail for count polling
    }
  },

  markAllAsSeen: async () => {
    try {
      await api.notificationsMarkAllAsSeen();
      const updated = get().notifications.map(n =>
        n.status === 'new' ? { ...n, status: 'seen' as const } : n,
      );
      set({ notifications: updated, newCount: 0 });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to mark notifications as seen';
      set({ error: message });
    }
  },

  hideNotification: async (notificationId: string) => {
    try {
      await api.notificationsHide(notificationId);
      const filtered = get().notifications.filter(
        n => n._id !== notificationId,
      );
      // Recalculate new count after hiding
      const newCount = filtered.filter(n => n.status === 'new').length;
      set({ notifications: filtered, newCount });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to hide notification';
      set({ error: message });
    }
  },

  reset: () => set(initialState),
}));

export default useNotificationsStore;
